import { __assign, __awaiter, __generator, __read, __rest, __spread, __values } from "tslib";
import { buildASTSchema, buildSchema } from 'graphql';
import { addResolversToSchema, addErrorLoggingToSchema } from '@graphql-tools/schema';
import { mergeTypeDefs } from './typedefs-mergers/merge-typedefs';
import { mergeResolvers } from './merge-resolvers';
import { SchemaDirectiveVisitor, asArray, getResolversFromSchema, } from '@graphql-tools/utils';
import { mergeExtensions, extractExtensionsFromSchema, applyExtensions } from './extensions';
var defaultResolverValidationOptions = {
    requireResolversForArgs: 'ignore',
    requireResolversForNonScalar: 'ignore',
    requireResolversForAllFields: 'ignore',
    requireResolversForResolveType: 'ignore',
    requireResolversToMatchSchema: 'ignore',
};
/**
 * Synchronously merges multiple schemas, typeDefinitions and/or resolvers into a single schema.
 * @param config Configuration object
 */
export function mergeSchemas(config) {
    var e_1, _a;
    var typeDefs = mergeTypes(config);
    var extractedResolvers = [];
    var extractedExtensions = [];
    try {
        for (var _b = __values(config.schemas), _c = _b.next(); !_c.done; _c = _b.next()) {
            var schema = _c.value;
            extractedResolvers.push(getResolversFromSchema(schema));
            extractedExtensions.push(extractExtensionsFromSchema(schema));
        }
    }
    catch (e_1_1) { e_1 = { error: e_1_1 }; }
    finally {
        try {
            if (_c && !_c.done && (_a = _b.return)) _a.call(_b);
        }
        finally { if (e_1) throw e_1.error; }
    }
    extractedResolvers.push.apply(extractedResolvers, __spread(ensureResolvers(config)));
    var resolvers = mergeResolvers(extractedResolvers, config);
    var extensions = mergeExtensions(extractedExtensions);
    return makeSchema({ resolvers: resolvers, typeDefs: typeDefs, extensions: extensions }, config);
}
/**
 * Synchronously merges multiple schemas, typeDefinitions and/or resolvers into a single schema.
 * @param config Configuration object
 */
export function mergeSchemasAsync(config) {
    return __awaiter(this, void 0, void 0, function () {
        var _a, typeDefs, resolvers, extensions;
        var _this = this;
        return __generator(this, function (_b) {
            switch (_b.label) {
                case 0: return [4 /*yield*/, Promise.all([
                        mergeTypes(config),
                        Promise.all(config.schemas.map(function (schema) { return __awaiter(_this, void 0, void 0, function () { return __generator(this, function (_a) {
                            return [2 /*return*/, getResolversFromSchema(schema)];
                        }); }); })).then(function (extractedResolvers) {
                            return mergeResolvers(__spread(extractedResolvers, ensureResolvers(config)), config);
                        }),
                        Promise.all(config.schemas.map(function (schema) { return __awaiter(_this, void 0, void 0, function () { return __generator(this, function (_a) {
                            return [2 /*return*/, extractExtensionsFromSchema(schema)];
                        }); }); })).then(function (extractedExtensions) {
                            return mergeExtensions(extractedExtensions);
                        }),
                    ])];
                case 1:
                    _a = __read.apply(void 0, [_b.sent(), 3]), typeDefs = _a[0], resolvers = _a[1], extensions = _a[2];
                    return [2 /*return*/, makeSchema({ resolvers: resolvers, typeDefs: typeDefs, extensions: extensions }, config)];
            }
        });
    });
}
function mergeTypes(_a) {
    var schemas = _a.schemas, typeDefs = _a.typeDefs, config = __rest(_a, ["schemas", "typeDefs"]);
    return mergeTypeDefs(__spread(schemas, (typeDefs ? asArray(typeDefs) : [])), config);
}
function ensureResolvers(config) {
    return config.resolvers ? asArray(config.resolvers) : [];
}
function makeSchema(_a, config) {
    var resolvers = _a.resolvers, typeDefs = _a.typeDefs, extensions = _a.extensions;
    var schema = typeof typeDefs === 'string' ? buildSchema(typeDefs, config) : buildASTSchema(typeDefs, config);
    // add resolvers
    if (resolvers) {
        schema = addResolversToSchema({
            schema: schema,
            resolvers: resolvers,
            resolverValidationOptions: __assign(__assign({}, defaultResolverValidationOptions), (config.resolverValidationOptions || {})),
        });
    }
    // use logger
    if (config.logger) {
        schema = addErrorLoggingToSchema(schema, config.logger);
    }
    // use schema directives
    if (config.schemaDirectives) {
        SchemaDirectiveVisitor.visitSchemaDirectives(schema, config.schemaDirectives);
    }
    // extensions
    applyExtensions(schema, extensions);
    return schema;
}
//# sourceMappingURL=merge-schemas.js.map